import React from "react";
import { useTranslation } from "react-i18next";
import { clampFillOpacity } from "../../../../utils/fillOpacity";
import {
  panelAppearanceRowClassName,
  panelRowLabelClassName,
} from "../propertyPanelUtils";

type OpacityFieldProps = {
  value: number;
  mixed?: boolean;
  mixedLabel?: string;
  onChange: (value: number) => void;
  onCommit?: () => void;
  /** Clears opacity back to 100% and remounts stuck transparent meshes. */
  onRestore?: () => void;
  disabled?: boolean;
  /** Defaults to fill-opacity for the fill section; override when reused elsewhere. */
  inputId?: string;
  ariaLabel?: string;
};

const OpacityField: React.FC<OpacityFieldProps> = ({
  value,
  mixed = false,
  mixedLabel,
  onChange,
  onCommit,
  onRestore,
  disabled = false,
  inputId = "fill-opacity",
  ariaLabel,
}) => {
  const { t } = useTranslation();
  const resolvedMixedLabel = mixedLabel ?? t("common.mixed");
  const resolvedAriaLabel = ariaLabel ?? t("propertyPanel.fillOpacity");
  const percentage = Math.round(clampFillOpacity(value) * 100);

  return (
    <div className="py-1.5">
      <div className={panelAppearanceRowClassName}>
        <label htmlFor={inputId} className={panelRowLabelClassName}>
          {t("propertyPanel.opacity")}
        </label>
        <div className="flex min-w-[88px] shrink-0 items-center justify-end gap-1.5">
          {onRestore && (
            <button
              type="button"
              className="text-[11px] font-medium leading-5 text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
              title={t("propertyPanel.restoreOpacityHint")}
              aria-label={t("propertyPanel.restoreOpacity")}
              disabled={disabled}
              onClick={onRestore}
            >
              {t("propertyPanel.restoreOpacity")}
            </button>
          )}
          <output
            className="text-right text-[11px] tabular-nums text-gray-500"
            htmlFor={inputId}
          >
            {mixed ? resolvedMixedLabel : `${percentage}%`}
          </output>
        </div>
      </div>
      <input
        id={inputId}
        aria-label={resolvedAriaLabel}
        aria-valuetext={mixed ? resolvedMixedLabel : `${percentage}%`}
        className="mt-1 h-2 w-full cursor-pointer appearance-none rounded-sm border border-gray-200 accent-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
        type="range"
        min={0}
        max={100}
        step={1}
        value={percentage}
        disabled={disabled}
        onChange={(event) => onChange(Number(event.target.value) / 100)}
        onPointerUp={onCommit}
        onKeyUp={onCommit}
        onBlur={onCommit}
      />
      <div className="mt-1 flex justify-between text-[11px] text-gray-400">
        <span>{t("propertyPanel.transparent")}</span>
        <span>{t("propertyPanel.opaque")}</span>
      </div>
    </div>
  );
};

export default OpacityField;
